import keyBy from 'lodash/keyBy';
import { BOARD_FIELD_CATEGORY } from '../../common/constants';
import type { ExtendedFields, BoardIssueFieldOld } from '../../common/types';
import type { IssueFieldsMap, IssueTypesMap, BoardIssueFieldUpdate } from './types';

export const isGroupByFieldAllowedByType = (
	fieldType: string,
	issueFields?: IssueFieldsMap,
	issueTypesMap?: IssueTypesMap,
) => {
	// Allow grouping if we don't know about the fields
	if (issueFields == null || issueTypesMap == null) {
		return true;
	}
	return Object.values(issueFields).some(
		(field) =>
			field.type === fieldType &&
			field.issueTypes != null &&
			field.issueTypes.some((issueTypeId) => issueTypesMap[issueTypeId]?.subtask === false),
	);
};

export const isGroupByFieldAllowedByKey = (
	fieldKey: string,
	issueFields?: IssueFieldsMap,
	issueTypesMap?: IssueTypesMap,
) => {
	// Allow grouping if we don't know about the fields
	if (issueFields == null || issueTypesMap == null) {
		return true;
	}
	return (
		issueFields[fieldKey] != null &&
		issueFields[fieldKey].issueTypes?.some(
			(issueTypeId) => issueTypesMap?.[issueTypeId]?.subtask === false,
		)
	);
};

export const updateExtendedFields = (
	existingDetails: ExtendedFields,
	updates: BoardIssueFieldUpdate[],
): ExtendedFields => {
	const updatesMap = keyBy(updates, 'fieldId');

	const toReturn: ExtendedFields = {
		fields: [],
		fieldsByAlias: { ...existingDetails.fieldsByAlias },
	};

	const updatedFieldIds = new Set([
		...updates.map((update) => update.fieldId),
		...existingDetails.fields.map((field) => field.fieldId),
	]);

	existingDetails.fields.forEach((field) => {
		updatedFieldIds.delete(field.fieldId);
		const fieldUpdates = updatesMap[field.fieldId];
		const shouldRemoveField =
			fieldUpdates != null && 'value' in fieldUpdates && fieldUpdates.value == null;
		if (shouldRemoveField) {
			return;
		}
		if (fieldUpdates != null) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const updatedField = {
				...field,
				...fieldUpdates,
			} as BoardIssueFieldOld;
			toReturn.fields.push(updatedField);
			if (updatedField.type === BOARD_FIELD_CATEGORY) {
				toReturn.fieldsByAlias.category = updatedField;
			}
		} else {
			toReturn.fields.push(field);
		}
	});

	updatedFieldIds.forEach((fieldId) => {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const updatedField = updatesMap[fieldId] as BoardIssueFieldOld;
		if (updatedField != null) {
			toReturn.fields.push(updatedField);
			if (updatedField.type === BOARD_FIELD_CATEGORY) {
				toReturn.fieldsByAlias.category = updatedField;
			}
		}
	});

	return toReturn;
};
